import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, IndianRupee, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

async function loadRazorpayScript() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Opens Razorpay Standard Checkout for the given plan + cycle.
 * On payment success → /payment/success?order_id=…
 * On failure/dismiss → /payment/cancel?order_id=…&reason=…
 */
export default function RazorpayCheckoutButton({ planSlug, billingCycle, label, highlight, testid }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const pay = async () => {
    if (!user) { navigate("/signup"); return; }
    setErr("");
    setBusy(true);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        setErr("Could not load Razorpay Checkout. Please retry.");
        setBusy(false);
        return;
      }
      const { data: order } = await api.post("/razorpay/create-order", {
        plan_slug: planSlug,
        billing_cycle: billingCycle,
      });

      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "AllBusinessOnline",
        description: `${order.plan_name} · ${billingCycle}`,
        prefill: { name: order.user.name || "", email: order.user.email || "" },
        theme: { color: "#38bdf8" },
        handler: async (response) => {
          try {
            await api.post("/razorpay/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate(`/payment/success?order_id=${response.razorpay_order_id}`);
          } catch (e) {
            const reason = formatApiErrorDetail(e.response?.data?.detail) || "verification-failed";
            navigate(`/payment/cancel?order_id=${response.razorpay_order_id}&reason=${encodeURIComponent(reason)}`);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            navigate(`/payment/cancel?order_id=${order.order_id}&reason=cancelled`);
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "payment-failed";
        navigate(`/payment/cancel?order_id=${order.order_id}&reason=${encodeURIComponent(reason)}`);
      });
      rzp.open();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not open checkout.");
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        data-testid={testid || `checkout-${planSlug}-${billingCycle}`}
        onClick={pay}
        disabled={busy}
        className={`w-full py-3 rounded-md font-medium transition inline-flex items-center justify-center gap-2 ${
          highlight
            ? "bg-sky-400 hover:bg-sky-300 text-slate-950"
            : "border border-sky-400 text-sky-100 hover:bg-sky-400 hover:text-slate-950"
        } disabled:opacity-60`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />}
        {busy ? "Opening…" : (label || "Pay with UPI / Card")}
      </button>
      {err && <p className="text-xs text-red-400 mt-2 text-center">{err}</p>}
      <p className="text-[10px] font-mono text-slate-500 mt-2 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> Secure checkout · UPI · Cards · Net Banking
      </p>
    </div>
  );
}
